import { useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { haveValue } from "../../helper/common";
import DatePicker from "react-datepicker";
import {
  Button,
  Card,
  ChoiceGroup,
  Control,
  Field,
  Icon,
  Skeleton,
  TextField,
} from "../design";

const PropertyRentDetails = (props) => {
  const { property, sharedProfile, formData, setFormData, handleNextStep } =
    props;
  const { _t } = useTranslator();

  const [error, setError] = useState(false);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth().toString().padStart(2, "0"); // Adding 2 to get month as it is zero-based and payment to start from next month

  const minDate = currentDate.toISOString().split("T")[0];

  const handleChangeInput = (e) => {
    if (error) {
      setError(false);
    }
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleReview = (e) => {
    e.preventDefault();
    if (
      !haveValue(formData?.payment_start) ||
      !haveValue(formData?.rent_amount) ||
      !haveValue(formData?.deposit_amount)
    ) {
      setError(true);
    } else {
      handleNextStep();
    }
  };

  // label text with the red star for required fields
  const requiredLabel = (key) => (
    <>
      {key} <span className="assign-required">*</span>
    </>
  );

  return (
    <>
      <header className="assign-page__head">
        <h1>{_t("rentalDetails")}</h1>
        <p>{_t("pleaseProvideYourRentalMoveInDetails")}</p>
      </header>
      <Card>
        <form onSubmit={handleReview}>
          {/* Address and tenant are read-only; grey box while they load */}
          {property ? (
            <TextField
              id="movein_address"
              label={requiredLabel(_t("moveInAddress"))}
              placeholder={_t("enterAddress")}
              name="movein_address"
              disabled
              value={`${property?.street} - ${
                Number.isNaN(parseInt(property?.street_number, 10))
                  ? "N/A"
                  : String(parseInt(property?.street_number, 10))
              }, ${property?.floor_number}-${property?.flat_number}`}
            />
          ) : (
            <Field label={requiredLabel(_t("moveInAddress"))}>
              <Skeleton height={44} radius="md" />
            </Field>
          )}
          <TextField
            id="tenant"
            label={requiredLabel(_t("tenant"))}
            placeholder={_t("enterTenantName")}
            name="tenant"
            disabled
            value={`${
              haveValue(sharedProfile?.tenantid?.fname)
                ? sharedProfile?.tenantid?.fname
                : ""
            } ${
              haveValue(sharedProfile?.tenantid?.lname)
                ? sharedProfile?.tenantid?.lname
                : ""
            }`}
          />

          <div className="assign-grid">
            <Field
              htmlFor="payment_start"
              label={requiredLabel(_t("leaseStartDate"))}
              error={error && !haveValue(formData?.payment_start)}
            >
              <Control>
                <div className="assign-date">
                  <DatePicker
                    id="payment_start"
                    name="payment_start"
                    selected={
                      formData?.payment_start
                        ? new Date(formData.payment_start)
                        : null
                    }
                    onChange={(date) =>
                      handleChangeInput({
                        target: {
                          name: "payment_start",
                          value: date ? date.toISOString().split("T")[0] : "",
                        },
                      })
                    }
                    dateFormat="dd/MM/yyyy"
                    minDate={minDate}
                    placeholderText="dd/mm/yyyy"
                    className={`date-input cursor-pointer ${
                      !haveValue(formData?.payment_start) ? "empty" : ""
                    }`}
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                  />
                  <span className="assign-date__icon" aria-hidden="true">
                    <Icon name="calendar" size={18} />
                  </span>
                </div>
              </Control>
            </Field>
            <Field htmlFor="lease_end" label={_t("leaseEndDate")}>
              <Control>
                <div className="assign-date">
                  <DatePicker
                    id="lease_end"
                    name="lease_end"
                    selected={
                      formData?.lease_end ? new Date(formData.lease_end) : null
                    }
                    onChange={(date) =>
                      handleChangeInput({
                        target: {
                          name: "lease_end",
                          value: date ? date.toISOString().split("T")[0] : "",
                        },
                      })
                    }
                    dateFormat="dd/MM/yyyy"
                    placeholderText={`dd/mm/yyyy`}
                    className={`date-input cursor-pointer ${
                      !haveValue(formData?.lease_end) ? "empty" : ""
                    }`}
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                  />
                  <span className="assign-date__icon" aria-hidden="true">
                    <Icon name="calendar" size={18} />
                  </span>
                </div>
              </Control>
            </Field>
          </div>

          <ChoiceGroup
            name="shared_household"
            legend={requiredLabel(
              _t(
                "isItASharedHousehold",
                "question asked while awarding property"
              )
            )}
            value={formData.shared_household}
            onChange={handleChangeInput}
            options={[
              { value: "yes", label: _t("yes") },
              { value: "no", label: _t("no") },
            ]}
          />

          <div className="assign-grid">
            <TextField
              id="rent_amount"
              type="number"
              name="rent_amount"
              label={requiredLabel(_t("rentalPaymentAmount"))}
              placeholder={_t("enterPaymentAmount")}
              onChange={handleChangeInput}
              value={formData.rent_amount}
              error={error && !haveValue(formData?.rent_amount)}
            />
            <TextField
              id="deposit_amount"
              type="number"
              name="deposit_amount"
              label={requiredLabel(_t("depositAmount"))}
              placeholder={_t("enterDepositAmount")}
              onChange={handleChangeInput}
              value={formData.deposit_amount}
              error={error && !haveValue(formData?.deposit_amount)}
            />
          </div>

          {error && (
            <p className="assign-error" role="alert">
              {_t("allFieldsAreMandatoryToBeFilled")}.
            </p>
          )}
          <div className="assign-actions">
            <Button type="submit" size="lg">
              {_t("reviewDetails")}
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
};

export default PropertyRentDetails;
